import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GetBusTransactionDetails } from "../../Services/BusAPI";

const BusPrintTicket = () => {
  const location = useLocation();
  const [data, setData] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const referenceNo = queryParams.get("ReferenceNo");
  const key = queryParams.get("key");
  const clientCode = queryParams.get("clientCode");

  useEffect(() => {
    if (referenceNo && key && clientCode) {
      fetchData();
    }
  }, [referenceNo, key, clientCode]);

  const fetchData = async () => {
    try {
      const params = {
        ClientCode: clientCode,
        Key: key,
        referenceNo: referenceNo,
      };

      const res = await GetBusTransactionDetails(params);
      setData(res);
    } catch (err) {
      console.error(err);
    }
  };

  // SAFE DATA EXTRACTION
  const booking = data?.booking || {};
  const passengers = data?.passengers || [];
  const bookingPoints = data?.bookingPoints || {};
  const fares = data?.fares || {};
  const payments = data?.payments?.[0] || {};

  //  HANDLE BOTH ARRAY & OBJECT
  const fare = Array.isArray(fares) ? fares[0] : fares || {};

  const totalBaseFare =
    (fare?.baseFare || 0) + (fare?.companyMarkUp || 0) + (fare?.gst || 0);

  // ✅ DATE FIX (handles invalid format)
  const parseDate = (dateTime) => {
    if (!dateTime) return null;

    const cleaned = dateTime
      .replace(/\s+/g, " ")
      .replace(/(AM|PM)$/i, " $1");

    const date = new Date(cleaned);
    return isNaN(date) ? null : date;
  };

  const formatTime = (dateTime) => {
    const date = parseDate(dateTime);
    if (!date) return "-";

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateTime) => {
    const date = parseDate(dateTime);
    if (!date) return "-";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const policyString = booking?.cancellationPolicy || "";
  const policies = policyString
    .split(";")
    .filter(Boolean)
    .map((item) => {
      const parts = item.split(":");

      const from = parts[0]?.trim();   // e.g. "48hours"
      const to = parts[1]?.trim();     // e.g. "72hours of bus start time"
      const charge = parts[2]?.trim(); // e.g. "30"

      let label = "";

      if (from === "Above") {
        label = `Within ${to}`;
      } else if (from === "0hours") {
        label = `Above 0 hours`;
      } else {
        label = `Between ${from} to ${to}`;
      }

      return {
        label,
        charge,
      };
    });

  // ✅ AUTO PRINT
  useEffect(() => {
    if (!data) return;

    const waitForRender = async () => {
      // Wait for React render
      await new Promise(resolve => requestAnimationFrame(resolve));

      // Wait for all images
      const images = Array.from(document.images);

      await Promise.all(
        images.map((img) => {
          if (img.complete) return Promise.resolve();

          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      // Small delay for layout stabilization
      setTimeout(() => {
        window.focus();
        window.print();
      }, 300);
    };

    waitForRender();
  }, [data]);

  return (
    <div
      style={{
        background: "rgb(250, 250, 250)",
        margin: "0 auto",
        padding: "20px",
        width: "1000px",
      }}
    >

      {/* HEADER */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
        }}
      >
        <tbody>
          <tr>
            <td style={{ width: "60%", padding: "15px 40px" }}>
              <img src="/assets/images/logo.png" width="250" alt="logo" />
            </td>

            <td style={{
              width: "40%",
              fontSize: "15px",
              textAlign: "right",
            }}>
              <p><strong>{data?.baseCompanyName || "-"}</strong></p>
              <p>Email: {data?.bookingConsultants?.[0].emailId}</p>
              <p>Phone: {data?.bookingConsultants?.[0]?.phoneNo}</p>
              <p>Address: {data?.baseCompanyAddress}</p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* BOOKING INFO */}
      <table
        style={{
          width: "100%",
          border: "1px solid rgb(51, 51, 51)",
          marginBottom: "20px",
        }}
      >
        <tbody>
          <tr>
            <td
              style={{
                width: "50%",
                padding: "10px",
              }}
            >
              <p>
                <p><strong>Booking PNR:</strong> {booking?.pnr}</p>
                <p><strong>Ticket No:</strong> {booking?.ticketId}</p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    style={{
                      color: booking?.bookingStatus === "Confirmed" ? "#28a745" : "#dc3545",
                    }}
                  >
                    <strong>{booking?.bookingStatus?.toUpperCase()}</strong>
                  </span>
                </p>
                {/* <p><strong>Booking Time:</strong> {booking?.createdOn}</p> */}
                <strong>Booking Time:</strong>{" "}
                {formatDate(booking?.createdOn)}{" "}
                <span>{formatTime(booking?.createdOn)}</span>
              </p>
            </td>

            <td
              style={{
                width: "50%",
                //border: "1px solid rgb(204, 204, 204)",
                border: "1px solid rgb(51, 51, 51)",
                padding: "8px",
                textAlign: "center"
              }}
            >
              <strong>{booking?.operatorName}</strong>
              <div>{booking?.busType}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* TRAVEL DETAILS */}
      <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px" }}>
        Journey Details
      </p>

      <table
        style={{
          width: "100%",
          border: "1px solid rgb(204, 204, 204)",
          marginBottom: "20px",
        }}
      >
        <thead>
          <tr style={{ background: "rgb(248,249,250)" }}>
            <th
              style={{
                border: "1px solid rgb(204,204,204)",
                padding: "8px",
              }}
            >
              Boarding Point
            </th>
            <th
              style={{
                border: "1px solid rgb(204,204,204)",
                padding: "8px",
              }}
            >
              Dropping Point
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                width: "50%",
                border: "1px solid rgb(204, 204, 204)",
                padding: "8px",
              }}
            >

              <strong>{booking?.sourceName}</strong>
              <p>{bookingPoints?.boardingPointAddress}</p>
              <p>{bookingPoints?.boardingPointLandMark}</p>
              <p>{bookingPoints?.boardingPointLocation}</p>
              <p>Phone : <strong>{bookingPoints?.boardingPointPhone}</strong></p>
              <p>Time : <strong>{formatDate(bookingPoints?.boardingPointTime)} {" "}
                {formatTime(bookingPoints?.boardingPointTime)}</strong></p>
            </td>

            <td
              style={{
                width: "50%",
                border: "1px solid rgb(204, 204, 204)",
                padding: "8px",
              }}
            >

              <strong>{booking?.destinationName}</strong>
              <p>{bookingPoints?.droppingPointAddress}</p>
              <p>{bookingPoints?.droppingPointLandMark}</p>
              <p>{bookingPoints?.droppingPointLocation}</p>
              <p>Phone : <strong>{bookingPoints?.droppingPointPhone}</strong></p>
              <p>Time : <strong>{formatDate(bookingPoints?.droppingPointTime)} {" "}
                {formatTime(bookingPoints?.droppingPointTime)}</strong></p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* PASSENGERS */}
      <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px" }}>
        Passenger Details
      </p>

      <table
        style={{
          width: "100%",
          //border: "1px solid rgb(204, 204, 204)",
          border: "1px solid rgb(204, 204, 204)",
          marginBottom: "20px",
        }}
      >
        <thead style={{ background: "rgb(250, 250, 250)", }}>
          <tr>
            <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Sr. No</th>
            <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Traveler Name</th>
            <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Gender</th>
            <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Age</th>
            <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Seat No</th>
          </tr>
        </thead>

        <tbody>
          {passengers.length > 0 ? (
            passengers.map((p, i) => (
              <tr key={i}>
                <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>{i + 1}</td>
                <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>{`${p.title} ${p.firstName} ${p.lastName}`}</td>
                <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>{p.gender}</td>
                <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>{p.age}</td>
                <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>{p.seatNo}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5">No Passengers</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* FARE */}
      <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px" }}>
        Fare Details
      </p>

      <table
        style={{
          width: "100%",
          border: "1px solid rgb(204, 204, 204)",
          marginBottom: "20px",
        }}
      >
        <tbody>

          <tr>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", }}>Bus Fare (Incl. Taxes)</td>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", textAlign: "right", }}>INR {totalBaseFare}</td>
          </tr>

          <tr>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", }}>Management Fees</td>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", textAlign: "right", }}>INR {fare?.managementFees || 0}</td>
          </tr>
          <tr>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", }}>Management Fees GST</td>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", textAlign: "right", }}>INR {fare?.managementFeesGST || 0}</td>
          </tr>
          <tr>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", }}>Total Fare</td>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", textAlign: "right", }}>INR {fare?.totalFare || 0} </td>
          </tr>
          <tr style={{ background: "rgb(248, 249, 250)" }}>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", }}><strong>Total Amount Paid</strong></td>
            <td style={{ padding: "8px", borderBottom: "1px solid rgb(204, 204, 204)", textAlign: "right", }}><strong>INR {payments?.amountPaid || 0}</strong> </td>
          </tr>
        </tbody>


      </table>

      <div className="col-lg-12 important_info">
        <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px" }}>
          Cancellation Policy
        </p>

        <p className="partialCancelation">
          Partial Cancellation is :{" "}
          <span
            style={{
              color: booking?.partialCancellationAllowed ? "green" : "red",
            }}
          >
            {booking?.partialCancellationAllowed ? "Allowed" : "Not Allowed"}
          </span>
        </p>


        <div className="table-responsive">
          <table
            style={{
              width: "100%",
              border: "1px solid rgb(204, 204, 204)",
              marginBottom: "20px",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Cancellation time</th>
                <th style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>Cancellation charges</th>
              </tr>
            </thead>

            <tbody>
              {policies.length > 0 ? (
                policies.map((p, index) => (
                  <tr key={index}>
                    <td
                      className="centerText"
                      style={{
                        border: "1px solid rgb(204, 204, 204)",
                        padding: "5px",
                        textAlign: "center"
                      }}
                    >{p.label} Hrs</td>
                    <td
                      className="centerText"
                      style={{
                        border: "1px solid rgb(204, 204, 204)",
                        padding: "5px",
                        textAlign: "center"
                      }}
                    >{p.charge}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2">No Cancellation Policy Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px" }}>
        Important Information
      </p>

      <ul style={{ margin: "20px" }}>
        <li>The departure time mentioned on the ticket are only tentative timings, Please re confirm with the service provider for actual pickup location and bus timing.</li>
        <li>Each passenger is allowed to carry one bag of upto 10 kgs and one personal item such as a laptop bag, handbag, or briefcase of upto 5 kgs.</li>
        <li>Passengers should not carry any goods like weapons, inflammable, firearms, ammunition, drugs, liquor, smuggled goods etc and any other articles that are prohibited under law.</li>
        <li>Bus Operator reserves the right to deny boarding or charge additional amount in case passenger is travelling with extra luggage than what is mentioned above.</li>
        <li>Cancellation of this ticket is NOT allowed after bus departure time.</li>
      </ul>

      {data?.bookingConsultants && (
        <>
          <p style={{ background: "#110942", color: "#dacd65", padding: "10px 10px", paddingLeft: "20px" }}>
            Customer Support
          </p>
          <p style={{ padding: "10px 0" }}>
            For any assistance related to your booking, cancellation requests, or travel queries, please contact our support team using the details below.
          </p>
          <table
            style={{
              border: "1px solid rgb(204, 204, 204)",
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                {["Name", "Email Id", "Phone Number"].map(
                  (heading, index) => (
                    <th
                      key={index}
                      style={{
                        border: "1px solid rgb(204, 204, 204)",
                        padding: "6px",
                        //background: "#110942",
                        background: "rgb(250, 250, 250)",
                        //color: "#dacd65",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      {heading}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {data.bookingConsultants?.map(
                (item, index) => (
                  <React.Fragment key={index}>
                    {/* Main Row */}
                    <tr>
                      <td
                        style={{
                          padding: "5px",
                          textAlign: "center",
                          textTransform: "uppercase",
                          border: "1px solid rgb(204, 204, 204)"
                        }}
                      >
                        {item.name}
                      </td>

                      <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>
                        {item.emailId || "NA"}
                      </td>

                      <td style={{ border: "1px solid rgb(204, 204, 204)", padding: "5px", textAlign: "center", }}>
                        {item.phoneNo || "NA"}
                      </td>
                    </tr>
                  </React.Fragment>
                )
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default BusPrintTicket;

